import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useDesktopMode } from "@/contexts/DesktopModeContext";
import { ROLE_NAV_CONFIG } from "@/lib/roles";
import {
  LayoutDashboard, BookOpen, FileText, Package, Receipt, Calculator, BarChart3,
  Users, ShieldCheck, Truck, Settings, CreditCard, Building2, ClipboardList,
  FolderOpen, Landmark, Wallet, BadgeIndianRupee, Zap, Contact, Upload, Download,
  Bell, Brain, Database, Plug, Paintbrush, Rocket, Link2, QrCode, GitCompare,
  Bot, Shield, Briefcase, User, Smartphone, Sparkles, Search, Mic, Gift, Award,
  Megaphone, IndianRupee, Target, ShoppingBag, GraduationCap, MessageSquare,
  Mail, Lightbulb, MessageSquarePlus, Monitor, Minimize2, ChevronDown, ChevronRight,
  Activity, RefreshCw, Code, LogOut, Calendar, TrendingUp, DollarSign, Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard, BookOpen, FileText, Package, Receipt, Calculator, BarChart3,
  Users, ShieldCheck, Truck, Settings, CreditCard, Building2, ClipboardList,
  FolderOpen, Landmark, Wallet, BadgeIndianRupee, Zap, Contact, Upload, Download,
  Bell, Brain, Database, Plug, Paintbrush, Rocket, Link2, QrCode, GitCompare,
  Bot, Shield, Briefcase, User, Smartphone, Sparkles, Search, Mic, Gift, Award,
  Megaphone, IndianRupee, Target, ShoppingBag, GraduationCap, MessageSquare,
  Mail, Lightbulb, MessageSquarePlus, Activity, RefreshCw, Code, Monitor,
};

const QUICK_SHORTCUTS = [
  { label: "New Invoice", url: "/dashboard/invoices", icon: FileText },
  { label: "New Sale", url: "/dashboard/sales", icon: Receipt },
  { label: "New Purchase", url: "/dashboard/purchase", icon: Calculator },
  { label: "Journal Entry", url: "/dashboard/journals", icon: ClipboardList },
  { label: "Ledgers", url: "/dashboard/ledgers", icon: BookOpen },
  { label: "GST Filing", url: "/dashboard/gst", icon: ShieldCheck },
];

const QUICK_REPORTS = [
  { label: "Trial Balance", url: "/dashboard/reports" },
  { label: "Profit & Loss", url: "/dashboard/reports" },
  { label: "Balance Sheet", url: "/dashboard/reports" },
  { label: "Cash Flow", url: "/dashboard/cash-flow" },
  { label: "GST Reports", url: "/dashboard/gst-reports" },
  { label: "Audit Logs", url: "/dashboard/audit-logs" },
];

export default function DesktopModeLayout() {
  const { user, role, roleLabel, logout } = useAuth();
  const { exitDesktopMode } = useDesktopMode();
  const navigate = useNavigate();
  const location = useLocation();
  const navGroups = ROLE_NAV_CONFIG[role];
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    navGroups.forEach((g) => { init[g.label] = true; });
    return init;
  });

  const toggleGroup = (label: string) => {
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const currentFY = "FY 2025-26";
  const now = new Date();
  const timeStr = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  const dateStr = now.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <div className="h-screen w-screen flex flex-col bg-background overflow-hidden select-none">
      {/* === TOP BAR === */}
      <div className="h-10 bg-primary flex items-center justify-between px-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-primary-foreground/20 flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-xs">A</span>
            </div>
            <span className="text-primary-foreground font-bold text-sm">AccouPro</span>
          </div>
          <div className="h-4 w-px bg-primary-foreground/20" />
          <span className="text-primary-foreground/80 text-xs font-medium">
            {user?.company || "AccouPro Platform"}
          </span>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-primary-foreground/70 text-xs">
            <Calendar className="h-3.5 w-3.5" />
            <span className="font-medium">{currentFY}</span>
            <div className="h-3 w-px bg-primary-foreground/20" />
            <span>{dateStr}</span>
            <div className="h-3 w-px bg-primary-foreground/20" />
            <Clock className="h-3.5 w-3.5" />
            <span>{timeStr}</span>
          </div>
          <div className="h-4 w-px bg-primary-foreground/20" />
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-primary-foreground/20 flex items-center justify-center">
              <span className="text-primary-foreground text-[10px] font-semibold">
                {user?.name?.split(" ").map((n) => n[0]).join("") || "U"}
              </span>
            </div>
            <div className="text-xs text-primary-foreground/80">
              <span className="font-medium">{user?.name}</span>
              <span className="text-primary-foreground/50 ml-1.5">({roleLabel})</span>
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={exitDesktopMode}
            className="h-7 px-2 text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10"
          >
            <Minimize2 className="h-3.5 w-3.5 mr-1" />
            <span className="text-xs">Exit Desktop</span>
          </Button>
        </div>
      </div>

      {/* === QUICK TOOLBAR === */}
      <div className="h-8 bg-card border-b border-border flex items-center px-2 gap-1 flex-shrink-0">
        {QUICK_SHORTCUTS.map((s) => (
          <button
            key={s.label}
            onClick={() => navigate(s.url)}
            className={cn(
              "flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors",
              location.pathname === s.url
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-muted"
            )}
          >
            <s.icon className="h-3 w-3" />
            {s.label}
          </button>
        ))}
        <div className="flex-1" />
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Monitor className="h-3.5 w-3.5 text-accent" />
          <span className="font-medium text-accent">Desktop Mode</span>
        </div>
      </div>

      {/* === MAIN AREA === */}
      <div className="flex-1 flex min-h-0">
        {/* LEFT: Accounting Menu */}
        <div className="w-52 bg-card border-r border-border flex flex-col flex-shrink-0">
          <ScrollArea className="flex-1 sidebar-scrollbar">
            <div className="py-1">
              {navGroups.map((group) => {
                const isExpanded = expandedGroups[group.label] !== false;
                return (
                  <div key={group.label}>
                    <button
                      onClick={() => toggleGroup(group.label)}
                      className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <span>{group.label}</span>
                      {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    </button>
                    {isExpanded && (
                      <div className="pb-1">
                        {group.items.map((item) => {
                          const Icon = ICON_MAP[item.icon] || LayoutDashboard;
                          const active = location.pathname === item.url;
                          return (
                            <button
                              key={item.url}
                              onClick={() => navigate(item.url)}
                              className={cn(
                                "w-full flex items-center gap-2 px-4 py-1.5 text-xs transition-colors",
                                active
                                  ? "bg-accent/10 text-accent font-medium border-r-2 border-accent"
                                  : "text-foreground/70 hover:text-foreground hover:bg-muted/60"
                              )}
                            >
                              <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                              <span className="truncate">{item.title}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
          <div className="border-t border-border p-2">
            <button
              onClick={logout}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span>Log Out</span>
            </button>
          </div>
        </div>

        {/* CENTER: Transaction Workspace */}
        <div className="flex-1 flex flex-col min-w-0">
          <ScrollArea className="flex-1">
            <div className="p-4">
              <Outlet />
            </div>
          </ScrollArea>
        </div>

        {/* RIGHT: Quick Reports & Shortcuts */}
        <div className="w-48 bg-card border-l border-border flex flex-col flex-shrink-0">
          <ScrollArea className="flex-1">
            <div className="p-3 space-y-4">
              {/* Quick Reports */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <BarChart3 className="h-3.5 w-3.5 text-accent" />
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Quick Reports</span>
                </div>
                <div className="space-y-0.5">
                  {QUICK_REPORTS.map((r) => (
                    <button
                      key={r.label}
                      onClick={() => navigate(r.url)}
                      className="w-full text-left px-2 py-1.5 text-xs text-foreground/70 hover:text-foreground hover:bg-muted/60 rounded transition-colors"
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick Stats */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <TrendingUp className="h-3.5 w-3.5 text-accent" />
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Today's Summary</span>
                </div>
                <div className="space-y-2">
                  <div className="bg-muted/50 rounded-lg p-2">
                    <p className="text-[10px] text-muted-foreground">Sales Today</p>
                    <p className="text-sm font-bold text-foreground">₹1,24,500</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-2">
                    <p className="text-[10px] text-muted-foreground">Purchases</p>
                    <p className="text-sm font-bold text-foreground">₹87,200</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-2">
                    <p className="text-[10px] text-muted-foreground">Cash Balance</p>
                    <p className="text-sm font-bold text-foreground">₹3,45,800</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-2">
                    <p className="text-[10px] text-muted-foreground">Pending Invoices</p>
                    <p className="text-sm font-bold text-foreground">12</p>
                  </div>
                </div>
              </div>

              {/* Keyboard Shortcuts */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Shortcuts</span>
                </div>
                <div className="space-y-1 text-[10px] text-muted-foreground">
                  <div className="flex justify-between"><span>New Invoice</span><Badge variant="outline" className="text-[9px] h-4 px-1">Ctrl+I</Badge></div>
                  <div className="flex justify-between"><span>New Sale</span><Badge variant="outline" className="text-[9px] h-4 px-1">Ctrl+S</Badge></div>
                  <div className="flex justify-between"><span>Search</span><Badge variant="outline" className="text-[9px] h-4 px-1">Ctrl+K</Badge></div>
                  <div className="flex justify-between"><span>Exit Desktop</span><Badge variant="outline" className="text-[9px] h-4 px-1">Esc</Badge></div>
                </div>
              </div>
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* === STATUS BAR === */}
      <div className="h-6 bg-primary/5 border-t border-border flex items-center justify-between px-3 text-[10px] text-muted-foreground flex-shrink-0">
        <div className="flex items-center gap-3">
          <span>Ready</span>
          <div className="h-2 w-2 rounded-full bg-green-500" />
          <span>Connected</span>
        </div>
        <div className="flex items-center gap-3">
          <span>AccouPro Desktop Mode</span>
          <span>v2.0</span>
        </div>
      </div>
    </div>
  );
}
